import { useRef } from 'react'
import { InView } from 'react-intersection-observer'
import { COLLECTION_2_0_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { Image } from 'src/components/ui/Image'
import { Statsig } from 'src/sdk/statsig'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'

interface InfluencerItem {
  image: string
  text: string
  name: string
  bio: string
}

export interface InfluencersSectionData {
  title?: string
  isActive?: boolean
  items?: InfluencerItem[]
}

interface InfluencersSectionProps extends InfluencersSectionData {
  section?: string
}

export function InfluencersSection({
  title = 'Quem entra.',
  items = [],
  section,
  isActive = false,
}: Readonly<InfluencersSectionProps>) {
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)
  const isCollection2Enabled = Statsig.useStatsigFeatureGateValue(
    COLLECTION_2_0_EXPERIMENT_ID
  )

  const hasDispatchedViewedEvent = useRef(false)
  const influencers = items.filter(Boolean).slice(0, 3)
  const influencersCount = influencers.length

  const listClassNameByCount: Record<number, string> = {
    1: 'grid grid-cols-1',
    2: 'grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6',
    3: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  }

  const listClassName =
    listClassNameByCount[influencersCount] ?? listClassNameByCount[3]

  if (influencers.length === 0) {
    return null
  }

  const sendPromoFeatureViewedEvent = () => {
    if (hasDispatchedViewedEvent.current) {
      return
    }

    hasDispatchedViewedEvent.current = true

    dispatchAmplitudeEvent({
      eventName: 'Promo Feature Viewed',
      eventData: {
        ...standardObject,
        ...getFeatureObject({
          section: section ?? 'Banner influencer',
          locationOnPage: 'bottom',
          name: 'Banner influencer',
        }),
      },
    })
  }

  if (!isActive || !isCollection2Enabled) {
    return null
  }

  return (
    <InView
      as="section"
      triggerOnce
      threshold={0.7}
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
      className="relative left-1/2 w-screen -translate-x-1/2 my-8 md:my-[70px] bg-dark01 pb-[87px] md:pb-0 pt-20 md:pt-[104px]"
      aria-label="Influencers"
    >
      <div className="mx-auto px-5 max-w-[1400px]">
        <p className="text-white text-[40px] md:text-[80px] leading-none font-bold mb-10 md:mb-7">
          {title}
        </p>

        <ul className={listClassName}>
          {influencers.map((influencer, index) => (
            <li
              key={`${influencer.name}-${index}`}
              className="flex gap-8 md:gap-[58px] flex-col md:flex-row"
            >
              <Image
                src={influencer.image}
                alt={influencer.name || influencer.text || 'Influencer'}
                loading="lazy"
                className="mx-auto md:mx-0 max-w-full w-[350px] h-[350px] md:w-[432px] md:h-[470px] object-cover shrink-0"
              />
              <div className="mt-3">
                <p className="text-white text-xl md:text-[32px] font-normal font-fraunces italic pb-4 ">
                  “{influencer.text}”
                </p>

                <div className="max-w-[871px] border-t border-white/15 mt-7 pt-7">
                  <p className="mt-3 text-white text-base md:text-2xl font-semibold leading-6">
                    {influencer.name}
                  </p>

                  <p className="mt-2 text-white/65 text-[11px] md:text-base uppercase">
                    {influencer.bio}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </InView>
  )
}
